import { useEffect, useRef } from 'react';
import { ArrowUpRight, Check, Sparkles, X } from 'lucide-react';
import { Link } from 'react-router-dom';
import type { UpdateLogEntry } from '../lib/update-logs';
import '../update-logs.css';

type Props = { entry: UpdateLogEntry; onDismiss: () => void };

export function LatestUpdatePopup({ entry, onDismiss }: Props) {
  const dialog = useRef<HTMLDialogElement>(null);
  const acknowledge = useRef<HTMLButtonElement>(null);
  useEffect(() => {
    const element = dialog.current;
    const opener = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    if (element && !element.open) {
      element.showModal();
      acknowledge.current?.focus({ preventScroll: true });
    }
    return () => {
      if (element?.open) element.close();
      if (opener?.isConnected) opener.focus({ preventScroll: true });
    };
  }, []);

  return <dialog ref={dialog} className="update-popup" aria-labelledby="latest-update-title" aria-describedby="latest-update-summary"
    onCancel={event => { event.preventDefault(); onDismiss(); }}
    onClick={event => { if (event.target === event.currentTarget) onDismiss(); }}>
    <article className="update-popup-content">
      <button className="update-popup-close" type="button" aria-label="Close latest update" onClick={onDismiss}><X size={19} /></button>
      <header className="update-popup-header">
        <img src="/brand/owl.svg" width="88" height="88" alt="Soonscreener owl" />
        <span className="updates-latest"><Sparkles size={12} /> New on Soonscreener</span>
        <h2 id="latest-update-title">{entry.title}</h2>
        <p id="latest-update-summary">{entry.summary}</p>
      </header>
      <ul className="updates-highlights update-popup-highlights">
        {entry.highlights.map(highlight => <li key={highlight.title}>
          <span className="updates-check" aria-hidden="true"><Check size={14} /></span>
          <div><h3>{highlight.title}</h3><p>{highlight.description}</p></div>
        </li>)}
      </ul>
      <footer className="update-popup-footer">
        <Link className="outline-button" to={`/update-logs#${entry.id}`} onClick={onDismiss}>View update logs<ArrowUpRight size={14} /></Link>
        <button ref={acknowledge} className="primary-button" type="button" onClick={onDismiss}>Got it<Check size={14} /></button>
      </footer>
    </article>
  </dialog>;
}
